import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { UserProfile } from "@/components/feature/Sidebar";
import { inputService } from "@/services/api";
import { mapDbInsumoToFrontend } from "@/services/mapping";

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

export default function DocenteHistorialPage() {
  const [user] = useState<UserProfile | null>(getStoredUser);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState<any[]>([]);

  useEffect(() => {
    async function loadData() {
      if (!user) return;
      try {
        setLoading(true);
        setError("");
        const data = await inputService.listSolicitados();
        if (Array.isArray(data)) {
          const mapped = data
            .map(mapDbInsumoToFrontend)
            .filter((t) =>
              (String(t.docenteId) === String(user.id) || t.docenteName === user.nombre) &&
              (t.estado === "entregado" || t.estado === "aprobado" || t.estado === "devuelto_correccion")
            );
          setRequests(mapped);
        }
      } catch (err: any) {
        console.error("Error loading historical inputs:", err);
        setError("Error al cargar historial: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [user]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="flex items-center justify-center w-8 h-8">
          <i className="ri-loader-4-line text-foreground-300 text-2xl animate-spin" />
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div>
        <button
          onClick={() => navigate("/docente")}
          className="text-xs font-medium text-foreground-500 hover:text-foreground-700 cursor-pointer mb-2 flex items-center gap-1"
        >
          <span className="flex items-center justify-center w-3 h-3">
            <i className="ri-arrow-left-line" />
          </span>
          Volver al panel
        </button>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">Historial de Respuestas</h1>
        <p className="text-sm text-foreground-500 mt-1">Informes enviados, aprobados y devueltos</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="kpi-card border border-background-200 bg-white p-4 rounded-xl">
          <p className="text-xs font-semibold text-primary-600 uppercase tracking-wider">Enviados</p>
          <p className="text-4xl font-bold text-primary-600 mt-1 animate-count-up">
            {requests.filter((t) => t.estado === "entregado").length}
          </p>
          <p className="text-xs text-primary-500 font-medium mt-1">en revisión</p>
        </div>
        <div className="kpi-card border border-background-200 bg-white p-4 rounded-xl">
          <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Aprobados</p>
          <p className="text-4xl font-bold text-emerald-600 mt-1 animate-count-up">
            {requests.filter((t) => t.estado === "aprobado").length}
          </p>
          <p className="text-xs text-emerald-500 font-medium mt-1">completados</p>
        </div>
        <div className="kpi-card border border-red-200/60 bg-red-50/20 p-4 rounded-xl border">
          <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">Devueltos</p>
          <p className="text-4xl font-bold text-red-600 mt-1 animate-count-up">
            {requests.filter((t) => t.estado === "devuelto_correccion").length}
          </p>
          <p className="text-xs text-red-500 font-medium mt-1">para corregir</p>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500 mr-2" /> Cargando historial...
        </div>
      ) : requests.length > 0 ? (
        <div className="space-y-3">
          {requests.map((ti, idx) => {
            const isExpanded = expandedId === ti.id;
            const estadoConfig = {
              entregado: { label: "Enviado", color: "text-primary-700", bg: "bg-primary-50", dot: "bg-primary-400", icon: "ri-send-plane-line text-primary-500" },
              aprobado: { label: "Aprobado", color: "text-emerald-700", bg: "bg-emerald-50", dot: "bg-emerald-500", icon: "ri-verified-badge-line text-emerald-500" },
              devuelto_correccion: { label: "Devuelto", color: "text-red-700", bg: "bg-red-50", dot: "bg-red-500", icon: "ri-arrow-go-back-line text-red-500" },
            }[ti.estado as string] || { label: ti.estado, color: "text-foreground-700", bg: "bg-background-50", dot: "bg-foreground-400", icon: "ri-file-text-line text-foreground-500" };

            return (
              <div key={ti.id} className="card-modern border border-background-200 bg-white p-4 rounded-xl animate-fade-in-up" style={{ animationDelay: `${idx * 40}ms` }}>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : ti.id)}
                  className="w-full text-left cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${estadoConfig.dot}`} />
                        <p className="text-sm font-bold text-foreground-900 truncate">{ti.tipoInsumo}</p>
                        <span className={`badge-modern ${estadoConfig.bg} ${estadoConfig.color} border border-transparent`}>
                          {estadoConfig.label}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-500">Estudiante: {ti.studentName} ({ti.studentCurso})</p>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-foreground-400">
                        <span className="flex items-center gap-1">
                          <span className="flex items-center justify-center w-3 h-3">
                            <i className="ri-calendar-line" />
                          </span>
                          Enviado: {ti.submittedAt || "—"}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="flex items-center justify-center w-3 h-3">
                            <i className="ri-timer-line" />
                          </span>
                          Límite: {ti.fechaLimite}
                        </span>
                      </div>
                    </div>
                    <span className="flex items-center justify-center w-5 h-5 flex-shrink-0">
                      <i className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line text-foreground-400`} />
                    </span>
                  </div>
                </button>

                {isExpanded && ti.respuesta && (
                  <div className="mt-4 pt-4 border-t border-background-200 animate-slide-down">
                    <p className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-2">Contenido del informe</p>
                    <div className="bg-background-50 rounded-xl p-4 border border-background-200">
                      <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">{ti.respuesta}</p>
                    </div>
                    {ti.estado === "devuelto_correccion" && (
                      <div className="mt-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/docente/solicitudes/${ti.id}`);
                          }}
                          className="btn-modern bg-orange-500 text-white hover:bg-orange-600 px-4 py-2 text-sm cursor-pointer rounded-xl font-medium"
                        >
                          Corregir y reenviar
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-3">
            <span className="flex items-center justify-center w-8 h-8">
              <i className="ri-history-line text-foreground-300 text-2xl" />
            </span>
          </div>
          <h3 className="text-base font-bold text-foreground-800 mb-1">Sin historial</h3>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">
            Aún no has enviado ningún informe. Cuando completes y envíes tu primera solicitud, aparecerá aquí.
          </p>
        </div>
      )}
    </div>
  );
}